package com.eladfinish.bible.contest.screens.pairMatching.ui

import androidx.compose.foundation.draganddrop.dragAndDropSource
import androidx.compose.foundation.draganddrop.dragAndDropTarget
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draganddrop.DragAndDropEvent
import androidx.compose.ui.draganddrop.DragAndDropTarget
import androidx.compose.ui.draganddrop.DragAndDropTransferData
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.layer.drawLayer
import androidx.compose.ui.graphics.rememberGraphicsLayer
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.extensions.makeBoldByAsterisksAnnotated
import com.eladfinish.bible.contest.screens.pairMatching.model.KeyItem
import com.eladfinish.bible.contest.screens.pairMatching.model.ValueItem
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import com.eladfinish.bible.contest.ui.extensions.LocalNavigationBarInset
import com.eladfinish.bible.contest.ui.extensions.navigationBarContentPadding
import com.eladfinish.bible.contest.ui.extensions.verticalEdgesFadeEffect
import kotlinx.coroutines.delay
import kotlin.time.Duration.Companion.milliseconds

// ─── Auto-scroll constants ────────────────────────────────────────────────────

private const val SCROLL_AMOUNT_PX = 50f
private const val SCROLL_DELAY_MS = 50L
private const val EDGE_BAND_FRACTION = 0.15f // top/bottom 15% of the keys list triggers auto-scroll

/**
 * Two-column drag-and-drop game.
 *
 * • Left column (keys)  — fixed drop targets.
 * • Right column (values) — shuffled, draggable items.
 *
 * Implemented with the official Compose Multiplatform drag-and-drop modifiers
 * ([dragAndDropSource] / [dragAndDropTarget]). The platform only differs in how the
 * dragged payload is packed/unpacked — see [pairMatchingTransferData] and
 * [readPairMatchingValueId].
 *
 * Auto-scroll: while the drag pointer sits in the top/bottom edge band of the KEYS column, that
 * column scrolls continuously in that direction (until the very top/bottom) and stops the moment the
 * pointer leaves the band, leaves the list, or the drag ends.
 *
 * @param isDragOnLongClick true → drag starts on long-press (built-in source); false → drag starts
 *   immediately on touch-down (custom [dragAndDropSourceOnTouch]).
 * @param onAttemptMatch called when a value is dropped onto a key.
 */
@Composable
fun PairMatchingTwoColumnGame(
    keyItems: List<KeyItem>,
    valueItems: List<ValueItem>,
    keyTitle: String,
    valueTitle: String,
    isDragOnLongClick: Boolean,
    onAttemptMatch: (keyId: Int, valueId: Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    val keysListState = rememberLazyListState()
    val valuesListState = rememberLazyListState()

    // positive = scroll down, negative = scroll up, 0 = stop
    var scrollDirection by remember { mutableIntStateOf(0) }

    // ID of the value currently being dragged — its in-list copy is faded while the drag is active.
    val draggingValueId = remember { mutableStateOf<Int?>(null) }

    // ID of the key under the shadow (guards onExited so leaving key A doesn't wipe a direction that
    // entering key B just set).
    var hoveredKeyId by remember { mutableStateOf<Int?>(null) }

    // Keys list bounds in root coordinates (px) — compared against the drag pointer's Y to detect
    // the top/bottom edge band.
    var keysTopPx by remember { mutableFloatStateOf(0f) }
    var keysBottomPx by remember { mutableFloatStateOf(0f) }
    val navigationBarInsetPx = with(LocalDensity.current) { LocalNavigationBarInset.current.toPx() }

    // While a direction is active, scroll the keys column every SCROLL_DELAY_MS. scrollBy clamps at
    // the list edges, so the loop simply idles once the very top/bottom is reached — giving
    // continuous scrolling for as long as the shadow stays in the edge band.
    LaunchedEffect(scrollDirection) {
        if (scrollDirection == 0) return@LaunchedEffect
        while (true) {
            keysListState.scrollBy(SCROLL_AMOUNT_PX * scrollDirection)
            delay(SCROLL_DELAY_MS.milliseconds)
        }
    }

    Row(modifier = modifier.fillMaxSize()) {

        // ── Keys column (drop targets) ────────────────────────────────────────
        // Only this column auto-scrolls. The scroll is driven by the drag pointer's Y: while it sits
        // in the top/bottom edge band of the keys list, we scroll that way continuously. This is
        // position-based (not overlay strips), so it's reliable and survives the list scrolling
        // under a stationary shadow.
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight(),
        ) {
            ColumnHeader(text = keyTitle)

            LazyColumn(
                state = keysListState,
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .onGloballyPositioned { coords ->
                        val bounds = coords.boundsInRoot()
                        keysTopPx = bounds.top
                        // The list runs behind the navigation bar, where no drag can reach:
                        // the bottom edge band starts above it.
                        keysBottomPx = bounds.bottom - navigationBarInsetPx
                    }
                    .verticalEdgesFadeEffect(
                        fadeHeight = 40.dp,
                        showTopFade = keysListState.canScrollBackward,
                        showBottomFade = keysListState.canScrollForward,
                        color = MaterialTheme.colorScheme.surface,
                    )
                    .mouseVerticalScrollbar(keysListState),
                contentPadding = navigationBarContentPadding(),
            ) {
                itemsIndexed(keyItems, key = { _, item -> item.id }) { _, key ->
                    var isHovering by remember { mutableStateOf(false) }

                    val dropTarget = remember(key.id) {
                        object : DragAndDropTarget {
                            // Recompute the scroll direction from the live drag Y. Called on enter
                            // AND on move so it keeps updating as the shadow travels and holds its
                            // value while the shadow is stationary at an edge.
                            fun update(event: DragAndDropEvent) {
                                scrollDirection = edgeScrollDirection(
                                    y = event.dragYInRoot(),
                                    top = keysTopPx,
                                    bottom = keysBottomPx,
                                    canScrollDown = keysListState.canScrollForward,
                                    canScrollUp = keysListState.canScrollBackward,
                                )
                            }

                            override fun onDrop(event: DragAndDropEvent): Boolean {
                                val valueId = event.readPairMatchingValueId() ?: return false
                                onAttemptMatch(key.id, valueId)
                                return true
                            }

                            override fun onEntered(event: DragAndDropEvent) {
                                isHovering = true
                                hoveredKeyId = key.id
                                update(event)
                            }

                            override fun onMoved(event: DragAndDropEvent) = update(event)

                            override fun onExited(event: DragAndDropEvent) {
                                isHovering = false
                                if (hoveredKeyId == key.id) {
                                    hoveredKeyId = null
                                    scrollDirection = 0
                                }
                            }

                            override fun onEnded(event: DragAndDropEvent) {
                                isHovering = false
                                if (hoveredKeyId == key.id) {
                                    hoveredKeyId = null
                                    scrollDirection = 0
                                }
                                // All key targets receive onEnded when the session ends — clear the fade.
                                draggingValueId.value = null
                            }
                        }
                    }

                    PairMatchingKeyItemView(
                        item = key,
                        isDragHovering = isHovering,
                        modifier = Modifier.dragAndDropTarget(
                            shouldStartDragAndDrop = { it.isPairMatchingDrag() },
                            target = dropTarget,
                        ),
                    )
                }
            }
        }

        // ── Values column (drag sources) ──────────────────────────────────────
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxHeight(),
        ) {
            ColumnHeader(text = valueTitle)
            LazyColumn(
                state = valuesListState,
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier
                    .fillMaxSize()
                    .verticalEdgesFadeEffect(
                        fadeHeight = 40.dp,
                        showTopFade = valuesListState.canScrollBackward,
                        showBottomFade = valuesListState.canScrollForward,
                        color = MaterialTheme.colorScheme.surface,
                    )
                    .mouseVerticalScrollbar(valuesListState),
                contentPadding = navigationBarContentPadding(),
            ) {
                itemsIndexed(valueItems, key = { _, item -> item.id }) { _, value ->
                    // Snapshot the item into a graphics layer so it can be drawn as the drag shadow.
                    val shadowLayer = rememberGraphicsLayer()

                    // Marks this value as the one being dragged (fades its in-list copy).
                    val startDragging: (Offset) -> DragAndDropTransferData? = {
                        draggingValueId.value = value.id
                        pairMatchingTransferData(value.id)
                    }

                    val dragModifier = when {
                        !value.isVisible -> Modifier

                        isDragOnLongClick -> Modifier.dragAndDropSource(
                            transferData = startDragging,
                        )

                        else -> Modifier
                            .drawWithContent {
                                shadowLayer.record { this@drawWithContent.drawContent() }
                                drawLayer(shadowLayer)
                            }
                            .dragAndDropSourceOnTouch(
                                drawDragDecoration = { drawLayer(shadowLayer) },
                                transferData = startDragging,
                            )
                    }

                    // Outermost alpha fades the on-screen copy while dragging; the shadow
                    // (recorded from drawContent, below this modifier) stays fully opaque.
                    val faded = if (value.id == draggingValueId.value) {
                        Modifier.alpha(0.4f)
                    } else {
                        Modifier
                    }

                    PairMatchingValueItemView(
                        item = value,
                        isDragging = false,
                        modifier = faded.then(dragModifier),
                    )
                }
            }
        }
    }
}

// ─── Auto-scroll helper ───────────────────────────────────────────────────────

/**
 * Decides the auto-scroll direction from the drag pointer's root Y against the keys-list bounds:
 * inside the bottom [EDGE_BAND_FRACTION] of the list → +1 (down), inside the top band → -1 (up),
 * otherwise 0. Honors the list's scrollability so it never fights a clamped edge.
 */
private fun edgeScrollDirection(
    y: Float,
    top: Float,
    bottom: Float,
    canScrollDown: Boolean,
    canScrollUp: Boolean,
): Int {
    if (y.isNaN() || bottom <= top) return 0
    val band = (bottom - top) * EDGE_BAND_FRACTION
    return when {
        y >= bottom - band && canScrollDown -> 1
        y <= top + band && canScrollUp -> -1
        else -> 0
    }
}

// ─── Header ───────────────────────────────────────────────────────────────────

@Composable
internal fun ColumnHeader(text: String) {
    Text(
        text = text.makeBoldByAsterisksAnnotated(),
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp),
        textAlign = TextAlign.Center,
        fontSize = 20.sp,
        style = MaterialTheme.typography.titleMedium,
        color = Color.Black,
    )
}

// ─── Preview ──────────────────────────────────────────────────────────────────

@Preview(showBackground = true, name = "TwoColumnGame — in progress")
@Composable
private fun TwoColumnGamePreview() {
    val keys = listOf(
        KeyItem(id = 0, rawText = "דוד"),
        KeyItem(id = 1, rawText = "שלמה"),
        KeyItem(id = 2, rawText = "שאול", matchedValueRaw = "גבעה"),
        KeyItem(id = 3, rawText = "רחבעם"),
    )
    val values = listOf(
        ValueItem(id = 10, rawText = "חברון"),
        ValueItem(id = 11, rawText = "ירושלים"),
        ValueItem(id = 12, rawText = "גבעה", isVisible = false),
        ValueItem(id = 13, rawText = "שכם"),
    )
    PreviewTheme {
        PairMatchingTwoColumnGame(
            keyItems = keys,
            valueItems = values,
            keyTitle = "מלך",
            valueTitle = "עיר בירה",
            isDragOnLongClick = false,
            onAttemptMatch = { _, _ -> },
        )
    }
}
